import type { Asset } from "schemas/generated/asset";
import type { AssetCardProps } from "./AssetCard";

import * as React from "react";

import SvgAssetsEmptyState from "@svg/empty-assets";

import {
  MAX_IMAGE_SIZE_BYTES,
  MAX_VIDEO_SIZE_BYTES,
} from "@/features/assets/constants";
import Label from "@replo/design-system/components/label/Label";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import twMerge from "@replo/design-system/utils/twMerge";
import { format } from "date-fns";
import InfiniteScroll from "react-infinite-scroll-component";
import { formatFileSize } from "replo-utils/lib/misc";
import { useRequiredContext } from "replo-utils/react/context";

import { AssetCard } from "./AssetCard";

type AssetsGridProps = {
  assets: Asset[];
  searchTerm: string;
  droppedAssets?: Asset[];
  onAssetSelected?: (asset: Asset) => void;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isLoading: boolean;
  isDragActive?: boolean;
  emptyState: React.ReactNode;
  shouldAllowActions?: boolean;
  className?: string;
};

const AssetGridContext = React.createContext<{
  internalAssets: Asset[];
  droppedAssets: Asset[];
  onAssetSelected: ((asset: Asset) => void) | undefined;
  shouldAllowActions: boolean;
} | null>(null);

const useAssetGridContext = () => useRequiredContext(AssetGridContext);

const AssetsGridRoot: React.FC<React.PropsWithChildren<AssetsGridProps>> = ({
  children,
  searchTerm,
  hasNextPage,
  fetchNextPage,
  isLoading,
  isDragActive = false,
  assets,
  droppedAssets = [],
  emptyState,
  onAssetSelected,
  shouldAllowActions = false,
  className,
}) => {
  const gridRef = React.useRef<HTMLDivElement>(null);

  // biome-ignore lint/correctness/useExhaustiveDependencies(searchTerm): searchTerm changing is used to reset the scroll position
  React.useEffect(() => {
    if (gridRef.current) {
      gridRef.current.scrollTop = 0;
    }
  }, [searchTerm]);

  // NOTE (Gabe 2025-03-06): internalAssets is used to prevent flashes when
  // search results are loading.
  const [internalAssets, setInternalAssets] = React.useState<Asset[]>(assets);
  React.useEffect(() => {
    if (assets.length > 0 || !isLoading) {
      setInternalAssets(assets);
    }
  }, [assets, isLoading]);

  if (
    internalAssets.length === 0 &&
    droppedAssets.length === 0 &&
    !searchTerm &&
    isLoading
  ) {
    return <AssetsGridSkeleton length={7} />;
  }

  if (internalAssets.length === 0 && droppedAssets.length === 0) {
    return emptyState;
  }

  const contextValue = {
    internalAssets,
    droppedAssets,
    onAssetSelected,
    shouldAllowActions,
  };

  return (
    <AssetGridContext.Provider value={contextValue}>
      <div
        id="assets-grid"
        className={twMerge(
          "flex flex-1 cursor-default flex-col overflow-scroll",
          className,
        )}
        ref={gridRef}
      >
        {isDragActive ? (
          <div className="bg-sheet border-0.5 border-border flex flex-1 flex-col items-center justify-center gap-3 self-stretch rounded-sm border-dashed mb-3">
            <SvgAssetsEmptyState />
            <div className="text-muted flex flex-col gap-1 p-2 text-center text-sm">
              <div className="header-h5">Drop assets here to upload</div>
              <div>Max {MAX_IMAGE_SIZE_BYTES / (1024 * 1024)}MB for images</div>
              <div> {MAX_VIDEO_SIZE_BYTES / (1024 * 1024)}MB for videos</div>
            </div>
          </div>
        ) : (
          <InfiniteScroll
            dataLength={assets.length}
            next={() => void fetchNextPage()}
            hasMore={hasNextPage}
            scrollableTarget="assets-grid"
            loader={<AssetsGridSkeleton length={3} />}
          >
            <div className="grid w-full gap-2 overflow-hidden pb-3 grid-cols-1 md:grid-cols-2">
              {children}
            </div>
          </InfiniteScroll>
        )}
      </div>
    </AssetGridContext.Provider>
  );
};

const AssetCardDetailed: React.FC<AssetCardProps> = (props) => {
  const { asset } = props;

  return (
    <AssetCard.Root {...props}>
      <AssetCard.Anchor
        shouldAllowDrag={props.shouldAllowDrag}
        onClick={props.onClick}
      >
        <AssetCard.AnchorFooter shouldAllowActions={props.shouldAllowActions} />
      </AssetCard.Anchor>
      <AssetCard.Content>
        <span className="w-full break-all text-sm font-medium">
          {asset.name}
        </span>
        <div className="text-muted flex gap-1 whitespace-nowrap text-sm">
          <div className="bg-selectable-selected rounded-sm">
            <Label
              label={
                <span className="text-primary px-1 py-0.5 uppercase">
                  {asset.fileExtension}
                </span>
              }
              size="sm"
              layoutClassName="w-fit h-4"
            />
          </div>
          {asset.width && asset.height && (
            <span>
              {asset.width}x{asset.height}
            </span>
          )}
          {asset.sizeBytes && (
            <>
              <span>•</span>
              <span>{formatFileSize(asset.sizeBytes)}</span>
            </>
          )}
        </div>
        {asset.createdAt && (
          <span className="text-muted text-sm">
            Added {format(asset.createdAt, "MMM d, yyyy")}
          </span>
        )}
      </AssetCard.Content>
    </AssetCard.Root>
  );
};

const AssetsGridListDetailed = () => {
  const { internalAssets, droppedAssets, onAssetSelected, shouldAllowActions } =
    useAssetGridContext();

  return (
    <>
      {droppedAssets?.map((asset: Asset) => (
        <AssetCardDetailed
          key={asset.id}
          isUploading
          asset={asset}
          shouldAllowActions={shouldAllowActions}
          shouldAllowDrag={!Boolean(onAssetSelected)}
        />
      ))}

      {internalAssets.map((asset: Asset) => (
        <AssetCardDetailed
          key={asset.id}
          asset={asset}
          onClick={
            onAssetSelected
              ? () => {
                  onAssetSelected(asset);
                }
              : undefined
          }
          shouldAllowActions={shouldAllowActions}
          shouldAllowDrag={!Boolean(onAssetSelected)}
          className="h-full"
        />
      ))}
    </>
  );
};

const AssetsGridListSimplified = () => {
  const { internalAssets, droppedAssets, onAssetSelected, shouldAllowActions } =
    useAssetGridContext();

  return (
    <>
      {droppedAssets?.map((asset: Asset) => (
        <AssetCard
          key={asset.id}
          isUploading
          asset={asset}
          shouldAllowActions={shouldAllowActions}
          shouldAllowDrag={!Boolean(onAssetSelected)}
        />
      ))}

      {internalAssets.map((asset: Asset) => (
        <AssetCard
          key={asset.id}
          asset={asset}
          onClick={
            onAssetSelected
              ? () => {
                  onAssetSelected(asset);
                }
              : undefined
          }
          shouldAllowActions={shouldAllowActions}
          shouldAllowDrag={!Boolean(onAssetSelected)}
          className="h-full"
        />
      ))}
    </>
  );
};

export const AssetsGrid = (props: AssetsGridProps) => {
  return (
    <AssetsGridRoot {...props}>
      <AssetsGridListSimplified />
    </AssetsGridRoot>
  );
};

AssetsGrid.Root = AssetsGridRoot;
AssetsGrid.ListDetailed = AssetsGridListDetailed;
AssetsGrid.ListSimplified = AssetsGridListSimplified;

const AssetsGridSkeleton: React.FC<{
  length: number;
}> = ({ length }) => {
  return (
    <div className="grid w-full gap-2 pb-3 grid-cols-1 md:grid-cols-2">
      {Array.from({ length }).map((_, index) => (
        <div key={index} className="flex flex-col gap-1">
          <Skeleton className="h-[105px] w-full" />
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-12" />
        </div>
      ))}
    </div>
  );
};
